import React, { useState, useRef, useEffect } from 'react';
import {
  List, CaretDown, SignOut, User,
  Factory, Drop, Anchor, Buildings, HardHat
} from '@phosphor-icons/react';
import NotificationCenter from './NotificationCenter';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../i18n/LanguageContext';

const INDUSTRY_ICONS = {
  'Manufacturing': Factory,
  'Oil and Gas': Drop,
  'Marine': Anchor,
  'Built Environment': Buildings,
  'Materials and Construction': HardHat,
};

export default function Header({ user, industry, setIndustry, industries, onMenuClick, onLogout, notifications, unreadCount, markRead, markAllRead, refetchNotifications }) {
  const { t } = useTranslation();
  const [showIndustry, setShowIndustry] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const industryRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (industryRef.current && !industryRef.current.contains(e.target)) setShowIndustry(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const IndustryIcon = INDUSTRY_ICONS[industry] || Factory;

  return (
    <header className="h-16 bg-surface border-b border-border flex items-center justify-between px-4 md:px-6 flex-shrink-0" data-testid="header">
      {/* Left: hamburger + title */}
      <div className="flex items-center gap-4">
        <button
          data-testid="menu-toggle"
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-background transition-colors text-text-secondary"
        >
          <List size={22} />
        </button>

        {/* Industry selector */}
        <div className="relative" ref={industryRef}>
          <button
            data-testid="industry-selector"
            onClick={() => setShowIndustry(!showIndustry)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:border-tertiary transition-all duration-200 bg-background"
          >
            <IndustryIcon size={18} weight="duotone" className="text-primary" />
            <span className="font-heading text-sm font-semibold text-text-primary hidden sm:inline">{t(`header.industries.${industry}`) || industry}</span>
            <CaretDown size={14} className={`text-tertiary transition-transform ${showIndustry ? 'rotate-180' : ''}`} />
          </button>

          {showIndustry && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-surface border border-border rounded-lg shadow-lg z-50 overflow-hidden animate-fade-in">
              <div className="p-2">
                <span className="block px-3 py-2 text-[10px] font-body font-semibold uppercase tracking-[0.2em] text-tertiary">{t('header.selectIndustry')}</span>
                {industries.map((ind) => {
                  const Icon = INDUSTRY_ICONS[ind] || Factory;
                  return (
                    <button
                      key={ind}
                      data-testid={`industry-option-${ind.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => { setIndustry(ind); setShowIndustry(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-body transition-all ${
                        industry === ind
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-text-primary hover:bg-background'
                      }`}
                    >
                      <Icon size={18} weight="duotone" />
                      {t(`header.industries.${ind}`) || ind}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: lang switcher + notifications + profile */}
      <div className="flex items-center gap-3">
        <LanguageSwitcher />

        <NotificationCenter
          notifications={notifications}
          unreadCount={unreadCount}
          markRead={markRead}
          markAllRead={markAllRead}
          refetch={refetchNotifications}
        />

        <div className="relative" ref={profileRef}>
          <button
            data-testid="profile-menu"
            onClick={() => setShowProfile(!showProfile)}
            className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg hover:bg-background transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={16} weight="fill" className="text-primary" />
            </div>
            <span className="hidden md:inline text-sm font-body font-medium text-text-primary">{user?.name || 'User'}</span>
            <CaretDown size={12} className="text-tertiary" />
          </button>

          {showProfile && (
            <div className="absolute top-full right-0 mt-2 w-56 bg-surface border border-border rounded-lg shadow-lg z-50 overflow-hidden animate-fade-in">
              <div className="p-3 border-b border-border">
                <p className="font-body text-sm font-medium text-text-primary">{user?.name}</p>
                <p className="font-body text-xs text-text-secondary">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-semibold rounded-full uppercase tracking-wider">
                  {user?.role}
                </span>
              </div>
              <button
                data-testid="logout-button"
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-body text-alert-critical hover:bg-alert-critical/5 transition-colors"
              >
                <SignOut size={16} />
                {t('header.signOut')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
